import axiosInstance from '../AxiosClient'
import {
  fundraiserAchievementShape,
  normalizeAchievement,
  normalizeCatalogItem,
  normalizeStatesMap,
  normalizeStateUnlock,
} from './models'
import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { z } from 'zod'

const ACHIEVEMENTS_ENDPOINT = '/achievements/'

const unwrapList = (data) => {
  const rows = Array.isArray(data) ? data : data?.results ?? []
  return rows.map(normalizeAchievement)
}

// GET /achievements/ - the fundraiser's full trophy case.
const listMine = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(ACHIEVEMENTS_ENDPOINT)
  return unwrapList(res.data)
})

// GET /achievements/unseen/ - achievements not yet shown in a banner.
const getUnseen = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(`${ACHIEVEMENTS_ENDPOINT}unseen/`)
  return unwrapList(res.data)
})

// GET /achievements/catalog/ - every achievement (earned + locked) for the journey path.
const getCatalog = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(`${ACHIEVEMENTS_ENDPOINT}catalog/`)
  const rows = Array.isArray(res.data) ? res.data : res.data?.results ?? []
  return rows.map(normalizeCatalogItem)
})

// GET /achievements/states/ - US map unlock progress for this fundraiser.
const getStates = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(`${ACHIEVEMENTS_ENDPOINT}states/`)
  return normalizeStatesMap(res.data)
})

// GET /achievements/unseen_states/ - state unlocks not yet celebrated.
const getUnseenStates = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(`${ACHIEVEMENTS_ENDPOINT}unseen_states/`)
  const rows = Array.isArray(res.data) ? res.data : res.data?.results ?? []
  return rows.map(normalizeStateUnlock)
})

// POST /achievements/mark_seen/ - acknowledge banners so they don't reappear.
const markSeen = createCustomServiceCall(
  {
    inputShape: { ids: z.array(z.string().uuid()) },
    outputShape: { updated: z.number() },
  },
  async ({ client, input }) => {
    const res = await client.post(`${ACHIEVEMENTS_ENDPOINT}mark_seen/`, { ids: input.ids })
    return res.data
  },
)

// POST /achievements/mark_states_seen/ - acknowledge state unlock celebrations.
const markStatesSeen = createCustomServiceCall(
  {
    inputShape: { ids: z.array(z.string().uuid()) },
    outputShape: { updated: z.number() },
  },
  async ({ client, input }) => {
    const res = await client.post(`${ACHIEVEMENTS_ENDPOINT}mark_states_seen/`, { ids: input.ids })
    return res.data
  },
)

export const AchievementApi = createApi(
  {
    baseUri: ACHIEVEMENTS_ENDPOINT,
    client: axiosInstance,
    models: {
      entity: fundraiserAchievementShape,
    },
  },
  {
    listMine,
    getUnseen,
    getCatalog,
    getStates,
    getUnseenStates,
    markSeen,
    markStatesSeen,
  },
)

export const achievementFunctions = () => {
  return {
    api: AchievementApi,
  }
}
